// The rail's two orders (SPEC-viewfinder §2.3) and the one left column that
// reads them (§2.4). Pure, like everything in `derive/`.
//
// Time is the default and is never switched automatically: „Poznań teraz to
// jednak w pierwszej kolejności czas". Distance is a tool the resident asks for
// from the toolbar, and it re-orders one already-built list rather than
// building a second one — so the rows, their labels and their silences are the
// axis's answer in either order, and only their sequence differs.
import type { AxisRow, Voice } from '../shared/model.ts';
import type { Order } from '../state/viewstate.ts';

/** Placeless rows sort after every positioned one, whatever their hour. */
const FAR = Number.POSITIVE_INFINITY;

/** A row that has a place on the axis of its own: an item, or a run of them. */
type Anchor = Extract<AxisRow, { kind: 'item' | 'muteRun' }>;

const isAnchor = (row: AxisRow): row is Anchor => row.kind === 'item' || row.kind === 'muteRun';

/**
 * How far a row is. An item's own distance; for a run, the distance of the
 * first row it collapsed, which is where a run stands in either order.
 */
const awayFrom = (row: Anchor, metres: Map<string, number | null>): number =>
  row.kind === 'item' ? (metres.get(row.item.id) ?? FAR) : (row.dist ?? FAR);

/** Whether a row speaks for a voice — a run speaks for every voice it collapsed. */
const speaksFor = (row: Anchor, voice: Voice): boolean =>
  row.kind === 'item' ? row.item.voice === voice : row.voices.includes(voice);

/**
 * The voice a row that stands for no place belongs to. The gap band is ZTM's —
 * it is the band ZTM leaves when it is not answering — and the tick belongs to
 * nobody, because it marks the real „teraz" rather than a source.
 */
const voiceOf = (row: AxisRow): Voice | null =>
  row.kind === 'note' ? row.voice : row.kind === 'gap' ? 'ztm' : null;

/**
 * The same rows, nearest first (§2.3).
 *
 * The kreska is the list's head here rather than a boundary, so every row goes
 * under it; that is the view's half, and it falls out of nothing being on the
 * past side. „starsze" is the view's half too, and goes last.
 *
 * What this decides is the three-way sequence: positioned rows nearest first,
 * then the placeless ones among themselves in time order, and — because
 * `Array.prototype.sort` is stable and the input is in time order — two rows at
 * the same distance keep the hour they had.
 *
 * A note and a gap have no distance of their own and they are not the same
 * thing as a placeless row: they are a voice's sentence ABOUT its rows, so they
 * keep their place relative to that voice's first row. A voice with no row at
 * all — which is exactly when a ZTM gap exists — puts its sentence directly
 * under the kreska, never above it.
 */
export function byDistance(rows: AxisRow[], metres: Map<string, number | null>): AxisRow[] {
  const anchors = rows.filter(isAnchor);
  const floats = rows.filter((row) => !isAnchor(row));
  const sorted = anchors.toSorted((a, b) => awayFrom(a, metres) - awayFrom(b, metres));

  // Under the kreska and above every row: the sentences of voices that have no
  // row left to sit beside.
  const head: AxisRow[] = [];
  const above = new Map<Anchor, AxisRow[]>();
  for (const row of floats) {
    const voice = voiceOf(row);
    const anchor = voice ? sorted.find((a) => speaksFor(a, voice)) : undefined;
    if (!anchor) {
      head.push(row);
      continue;
    }
    const waiting = above.get(anchor);
    if (waiting) waiting.push(row);
    else above.set(anchor, [row]);
  }

  return [...head, ...sorted.flatMap((row) => [...(above.get(row) ?? []), row])];
}

/**
 * The one left column, per order (§2.4): the value the list is ordered by on
 * top, the other under it. 60 px holds both — `relativeTime`'s widest output,
 * `+12 h 10`, is 57,61 px at 12 px bold, and with only in-radius rows on the
 * list a distance can never be wider than `5,0 km?`.
 *
 * It is here rather than in the view because which number is loud is a fact
 * about the order, and the order is the derivation's input.
 */
export function column(
  row: Extract<AxisRow, { kind: 'item' }>,
  order: Order,
): { primary: string; secondary: string } {
  return order === 'odleglosc'
    ? { primary: row.distance, secondary: row.time }
    : { primary: row.time, secondary: row.distance };
}
